import {
  ActionEmptyState,
  ActionConfirmationModal,
  Button,
  LoadingSpinner,
  Text,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import {
  retrieveObjectUpdatesInfiniteQueryOptions,
  useDeleteObjectUpdate,
} from "@/lib/data/react-query/updates/object-updates";
import type {
  ObjectUpdate,
  UpdateAnchorType,
} from "@/lib/data/schemas/updates/ObjectUpdateSchema";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
  PAGE_PERMISSION_KEYS,
} from "@/lib/data/static/permissionOptions";
import { cn } from "@/lib/utils/cn";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useBlocker } from "@tanstack/react-router";
import { History } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION } from "@/lib/data/static/slideoverStatics";
import {
  ObjectUpdateComposer,
  type UpdateComposerState,
} from "./ObjectUpdateComposer";
import { ObjectUpdateCard } from "./ObjectUpdateCard";

interface ObjectUpdatesProps {
  anchorObjectType: UpdateAnchorType;
  anchorObjectId: string;
  bareLayout?: boolean;
  enabled?: boolean;
  autoFocusComposer?: boolean;
  onComposerStateChange?: (state: UpdateComposerState) => void;
}

export function ObjectUpdates(props: ObjectUpdatesProps) {
  return (
    <ObjectUpdatesContent
      key={`${props.anchorObjectType}:${props.anchorObjectId}`}
      {...props}
    />
  );
}

function ObjectUpdatesContent({
  anchorObjectType,
  anchorObjectId,
  bareLayout = false,
  enabled = true,
  autoFocusComposer = false,
  onComposerStateChange,
}: ObjectUpdatesProps) {
  const { hasPagePermission, hasActionPermission } = useUser();
  const [composerStates, setComposerStates] = useState<
    Partial<Record<string, UpdateComposerState>>
  >({});
  const [activeEditors, setActiveEditors] = useState<
    Record<string, ObjectUpdate>
  >({});
  const handleEditingChange = useCallback(
    (update: ObjectUpdate, editing: boolean) => {
      setActiveEditors((previous) => {
        if (editing) return { ...previous, [update.id]: update };
        return Object.fromEntries(
          Object.entries(previous).filter(([id]) => id !== update.id),
        );
      });
    },
    [],
  );
  const [updateToDelete, setUpdateToDelete] = useState<ObjectUpdate | null>(
    null,
  );
  const deleteUpdate = useDeleteObjectUpdate();
  const hasUnsavedDraft = Object.values(composerStates).some(
    (state) => state?.isDirty,
  );
  const isWorking =
    deleteUpdate.isPending ||
    Object.values(composerStates).some((state) => state?.isSubmitting);
  const blocker = useBlocker({ condition: hasUnsavedDraft || isWorking });
  const reportComposerState = useCallback(
    (id: string, state: UpdateComposerState) => {
      setComposerStates((previous) => {
        if (
          previous[id]?.isDirty === state.isDirty &&
          previous[id].isSubmitting === state.isSubmitting
        )
          return previous;
        return { ...previous, [id]: state };
      });
    },
    [],
  );
  const reportCreateState = useCallback(
    (state: UpdateComposerState) => {
      reportComposerState("create", state);
    },
    [reportComposerState],
  );
  const reportEditState = useCallback(
    (id: string, state: UpdateComposerState) => {
      reportComposerState(`update:${id}`, state);
    },
    [reportComposerState],
  );

  useEffect(() => {
    onComposerStateChange?.({
      isDirty: hasUnsavedDraft,
      isSubmitting: isWorking,
    });
  }, [hasUnsavedDraft, isWorking, onComposerStateChange]);
  useEffect(
    () => () => {
      onComposerStateChange?.({ isDirty: false, isSubmitting: false });
    },
    [onComposerStateChange],
  );
  useEffect(() => {
    if (blocker.status === "blocked" && !hasUnsavedDraft && !isWorking)
      blocker.proceed();
  }, [blocker, hasUnsavedDraft, isWorking]);
  const canView =
    hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailUpdates) &&
    (anchorObjectType !== "matter_expense" ||
      hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailCosts));
  const {
    data,
    isPending,
    isError,
    isFetchNextPageError,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery({
    ...retrieveObjectUpdatesInfiniteQueryOptions({
      anchorObjectType,
      anchorObjectId,
    }),
    enabled: enabled && canView && Boolean(anchorObjectId),
  });
  const updates = data?.pages.flatMap((page) => page.data.items) ?? [];
  const displayedUpdates = [
    ...updates,
    ...Object.values(activeEditors).filter(
      (update) => !updates.some((loaded) => loaded.id === update.id),
    ),
  ];
  const canCreate =
    Boolean(data?.pages[0]?.data.canCreate) &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.objectUpdate,
      ACTION_PERMISSION_VALUES.create,
    );

  if (!canView) {
    return <Text>You do not have access to view updates.</Text>;
  }

  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-col gap-4 overflow-hidden",
        !bareLayout && "bg-accent rounded-md p-4",
      )}
    >
      <div>
        <Text variant="subHeading">Newest first</Text>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-2 pb-2">
        {canCreate ? (
          <ObjectUpdateComposer
            anchorObjectType={anchorObjectType}
            anchorObjectId={anchorObjectId}
            autoFocus={autoFocusComposer}
            disabled={!enabled}
            onStateChange={reportCreateState}
          />
        ) : null}
        {isPending ? (
          <div
            className="flex min-h-48 items-center justify-center"
            role="status"
          >
            <LoadingSpinner className="h-6 w-6" />
            <span className="sr-only">Loading updates</span>
          </div>
        ) : (
          displayedUpdates.map((update) => (
            <ObjectUpdateCard
              key={update.id}
              update={update}
              onComposerStateChange={reportEditState}
              onEditingChange={handleEditingChange}
              onDelete={() => {
                if (update.canDelete) setUpdateToDelete(update);
              }}
            />
          ))
        )}
        {isError ? (
          <div className="flex flex-col items-center gap-3 py-4" role="alert">
            <Text className="text-destructive">
              {isFetchNextPageError
                ? "Could not load older updates."
                : "Could not load updates."}
            </Text>
            <Button
              variant="outline"
              disabled={isFetching}
              onClick={() => {
                if (isFetchNextPageError) {
                  void fetchNextPage();
                } else {
                  void refetch();
                }
              }}
            >
              Try again
            </Button>
          </div>
        ) : !isPending && updates.length === 0 ? (
          <ActionEmptyState
            title="No updates yet"
            description={
              anchorObjectType === "matter_expense"
                ? "Past updates to this expense will appear here."
                : "Past updates to this matter and its expenses will appear here."
            }
            icon={<History className="h-6 w-6" />}
          />
        ) : null}
        {hasNextPage && !isError ? (
          <div className="flex justify-center py-2">
            <Button
              variant="outline"
              disabled={isFetching}
              onClick={() => void fetchNextPage()}
            >
              {isFetchingNextPage ? (
                <>
                  <LoadingSpinner className="h-4 w-4" />
                  Loading older updates…
                </>
              ) : (
                "Load older updates"
              )}
            </Button>
          </div>
        ) : null}
      </div>
      <ActionConfirmationModal
        open={blocker.status === "blocked" && !isWorking}
        setOpen={(open) => {
          if (!open && blocker.status === "blocked") blocker.reset();
        }}
        title="Unsaved changes"
        description={SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION}
        buttonText="Confirm"
        actionVariant="default"
        afterActionFn={() => {
          if (blocker.status === "blocked") blocker.proceed();
        }}
      />
      <ActionConfirmationModal
        open={Boolean(updateToDelete)}
        setOpen={(open) => {
          if (!open && !deleteUpdate.isPending) setUpdateToDelete(null);
        }}
        title="Are you sure you want to delete this update?"
        description="This action cannot be undone."
        buttonText="Delete update"
        isActionPending={deleteUpdate.isPending}
        afterActionFn={async () => {
          if (!updateToDelete?.canDelete || deleteUpdate.isPending) return;
          await deleteUpdate.mutateAsync(updateToDelete.id);
        }}
      />
    </div>
  );
}
